import React from "react";
import { X, Loader2, Phone, Mail, MapPin, Package, Clock, DollarSign, ClipboardCheck, MessageSquare, Send, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

const DELIVERY_TYPES = [
  { value: "same_day", label: "Samme dag" },
  { value: "express", label: "Express (90 min)" },
  { value: "evening", label: "Aften" },
  { value: "business", label: "Erhverv" },
];

const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00", "12:00", "13:00",
  "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00", "22:00",
];

const empty = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  company_name: "",
  cvr: "",
  pickup_business_name: "",
  pickup_reference: "",
  pickup_address: "",
  pickup_city: "",
  pickup_zip: "",
  delivery_address: "",
  delivery_city: "",
  delivery_zip: "",
  delivery_access_info: "",
  recipient_name: "",
  recipient_phone: "",
  package_description: "",
  weight_kg: "5",
  delivery_type: "same_day",
  pickup_date: new Date().toISOString().slice(0, 10),
  pickup_time: "18:00",
  price_dkk: "",
  driver_notes: "",
  internal_notes: "",
  send_link_via: "email",
};

export default function ManualBookingModal({ open, onClose, onCreated }) {
  const [form, setForm] = React.useState(empty);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setForm(empty);
      setResult(null);
    }
  }, [open]);

  if (!open) return null;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["customer_name", "customer_email", "customer_phone", "pickup_address", "pickup_city", "pickup_zip", "delivery_address", "delivery_city", "delivery_zip", "package_description", "weight_kg", "pickup_time", "price_dkk"];
    const missing = required.filter((k) => !String(form[k] || "").trim());
    if (missing.length) {
      toast.error(`Udfyld: ${missing.join(", ")}`);
      return;
    }
    if (Number(form.price_dkk) <= 0) {
      toast.error("Pris skal være over 0");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        weight_kg: parseFloat(form.weight_kg),
        price_dkk: parseFloat(form.price_dkk),
      };
      // Strip empty optionals
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      const { data } = await adminApi().post("/admin/bookings/manual", payload);
      setResult(data);
      toast.success(`Ordre ${data.booking_number} oprettet — link sendt: ${data.sent_email ? "email ✓" : ""} ${data.sent_sms ? "SMS ✓" : ""}`);
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke oprette ordre");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.payment_url) return;
    try {
      await navigator.clipboard.writeText(result.payment_url);
      toast.success("Betalingslink kopieret");
    } catch {
      toast.error("Kunne ikke kopiere");
    }
  };

  // Success view
  if (result) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="manual-booking-success">
        <div className="bg-white border border-brand max-w-2xl w-full p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">✓ Ordre oprettet</div>
              <h2 className="font-display font-bold text-2xl text-brand-dark mt-1">{result.booking_number}</h2>
            </div>
            <button onClick={() => { setResult(null); onClose?.(); }} className="text-brand-text2 hover:text-brand-dark" data-testid="mb-success-close">
              <X size={20} />
            </button>
          </div>

          <div className="bg-emerald-50 border border-emerald-300 p-4 mb-4">
            <div className="text-xs uppercase tracking-widest text-emerald-900 font-bold mb-2">Betalingslink</div>
            <div className="font-mono text-xs text-emerald-900 break-all bg-white border border-emerald-200 p-2" data-testid="mb-payment-url">{result.payment_url}</div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={copyLink} className="text-xs border border-emerald-700 text-emerald-900 hover:bg-emerald-700 hover:text-white px-3 py-2 flex items-center gap-1" data-testid="mb-copy-link">
                <Copy size={12} /> Kopier link
              </button>
              <a href={result.payment_url} target="_blank" rel="noreferrer" className="text-xs border border-emerald-700 text-emerald-900 hover:bg-emerald-700 hover:text-white px-3 py-2 flex items-center gap-1" data-testid="mb-open-link">
                <ExternalLink size={12} /> Åbn (preview)
              </a>
            </div>
          </div>

          <div className="text-sm text-brand-text2 space-y-1">
            <div>📧 Email til kunden: <b>{result.sent_email ? "✓ sendt" : "—"}</b></div>
            <div>📱 SMS til kunden: <b>{result.sent_sms ? "✓ sendt" : "—"}</b></div>
            <div>💰 Beløb: <b>{result.amount_dkk.toFixed(0)} kr.</b></div>
            <div className="mt-3 text-xs text-brand-text2">
              Når kunden betaler, opdateres status automatisk til <b>&quot;Betalt&quot;</b> og ordren tildeles næste tilgængelige chauffør.
              Tracking: <a href={result.tracking_url} target="_blank" rel="noreferrer" className="text-brand-red underline">{result.tracking_url}</a>
            </div>
          </div>

          <div className="mt-5 flex gap-2">
            <button onClick={() => { setResult(null); setForm(empty); }} className="btn-secondary text-sm" data-testid="mb-new">Opret en til</button>
            <button onClick={() => { setResult(null); onClose?.(); }} className="btn-primary text-sm" data-testid="mb-done">Færdig</button>
          </div>
        </div>
      </div>
    );
  }

  // Form view
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="manual-booking-modal">
      <div className="bg-white border border-brand max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="bg-brand-dark text-white p-5 flex items-start justify-between gap-3 sticky top-0 z-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-300 font-bold">Manuel ordre</div>
            <h2 className="font-display font-bold text-2xl mt-1">Opret booking manuelt</h2>
            <p className="text-xs text-white/70 mt-1">For kunder der ringer/mailer i stedet for at booke selv. Stripe-link genereres automatisk og sendes til kunden.</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="mb-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-6">
          {/* Kunde */}
          <Section title="Kunde" icon={<Mail size={14} />}>
            <Field label="Navn *" value={form.customer_name} onChange={upd("customer_name")} placeholder="Fornavn Efternavn" testId="mb-customer-name" />
            <Field label="Email *" type="email" value={form.customer_email} onChange={upd("customer_email")} placeholder="kunde@email.dk" testId="mb-customer-email" />
            <Field label="Telefon *" value={form.customer_phone} onChange={upd("customer_phone")} placeholder="+45 12 34 56 78" testId="mb-customer-phone" />
            <Field label="Firma (valgfri)" value={form.company_name} onChange={upd("company_name")} placeholder="JYSK ApS" testId="mb-company" />
            <Field label="CVR (valgfri)" value={form.cvr} onChange={upd("cvr")} placeholder="12345678" testId="mb-cvr" />
          </Section>

          {/* Afhentning */}
          <Section title="Afhentning" icon={<MapPin size={14} />}>
            <Field label="Butik / firma (valgfri)" value={form.pickup_business_name} onChange={upd("pickup_business_name")} placeholder="JYSK Taastrup" testId="mb-pickup-business" />
            <Field label="Ordre-/kvitteringsnr (valgfri)" value={form.pickup_reference} onChange={upd("pickup_reference")} placeholder="JYSK-789456" testId="mb-pickup-ref" />
            <Field label="Adresse *" value={form.pickup_address} onChange={upd("pickup_address")} placeholder="Taastrup Hovedgade 100" testId="mb-pickup-addr" />
            <Field label="By *" value={form.pickup_city} onChange={upd("pickup_city")} placeholder="Taastrup" testId="mb-pickup-city" />
            <Field label="Postnr *" value={form.pickup_zip} onChange={upd("pickup_zip")} placeholder="2630" testId="mb-pickup-zip" />
          </Section>

          {/* Levering */}
          <Section title="Levering" icon={<Send size={14} />}>
            <Field label="Adresse *" value={form.delivery_address} onChange={upd("delivery_address")} placeholder="Eksempelvej 5" testId="mb-delivery-addr" />
            <Field label="By *" value={form.delivery_city} onChange={upd("delivery_city")} placeholder="København SV" testId="mb-delivery-city" />
            <Field label="Postnr *" value={form.delivery_zip} onChange={upd("delivery_zip")} placeholder="2450" testId="mb-delivery-zip" />
            <Field label="Adgang (opgang, etage, dørkode, elevator)" value={form.delivery_access_info} onChange={upd("delivery_access_info")} placeholder="Opgang 2, 3.tv, dørkode 1234" textarea testId="mb-access" />
            <Field label="Modtagernavn (valgfri)" value={form.recipient_name} onChange={upd("recipient_name")} placeholder="Hvis ikke samme som kunden" testId="mb-recipient-name" />
            <Field label="Modtager-telefon (valgfri)" value={form.recipient_phone} onChange={upd("recipient_phone")} testId="mb-recipient-phone" />
          </Section>

          {/* Vare */}
          <Section title="Vare & timing" icon={<Package size={14} />}>
            <Field label="Beskrivelse *" value={form.package_description} onChange={upd("package_description")} placeholder="Sofa-pude XL + lampe" textarea testId="mb-package-desc" />
            <Field label="Vægt (kg) *" type="number" step="0.1" value={form.weight_kg} onChange={upd("weight_kg")} testId="mb-weight" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select label="Service" value={form.delivery_type} onChange={upd("delivery_type")} options={DELIVERY_TYPES} testId="mb-service" />
              <Field label="Dato *" type="date" value={form.pickup_date} onChange={upd("pickup_date")} testId="mb-date" />
              <Select label="Tidspunkt *" value={form.pickup_time} onChange={upd("pickup_time")} options={TIME_SLOTS.map((t) => ({ value: t, label: t }))} testId="mb-time" />
            </div>
          </Section>

          {/* Pris + chauffør info + send-mode */}
          <Section title="Pris & instruks" icon={<DollarSign size={14} />}>
            <Field label="Pris (DKK, inkl. moms) *" type="number" step="1" value={form.price_dkk} onChange={upd("price_dkk")} placeholder="299" testId="mb-price" />
            <Field label="Note til chauffør (synlig)" value={form.driver_notes} onChange={upd("driver_notes")} placeholder="Ring 5 min før ankomst" textarea testId="mb-driver-notes" />
            <Field label="Intern note (kun admin)" value={form.internal_notes} onChange={upd("internal_notes")} textarea testId="mb-internal-notes" />
            <Select label="Send link til kunden via" value={form.send_link_via} onChange={upd("send_link_via")} options={[
              { value: "email", label: "Email" },
              { value: "sms", label: "SMS (kun hvis Twilio er sat op)" },
              { value: "both", label: "Email + SMS" },
              { value: "none", label: "Ingen — jeg sender selv" },
            ]} testId="mb-send-via" />
          </Section>

          <div className="flex gap-2 sticky bottom-0 bg-white pt-4 border-t border-brand">
            <button type="button" onClick={onClose} className="btn-secondary text-sm" disabled={busy} data-testid="mb-cancel">Annuller</button>
            <button type="submit" disabled={busy} className="btn-primary text-sm flex items-center gap-2 ml-auto" data-testid="mb-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
              Opret ordre & generér betalingslink
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-3 flex items-center gap-2">
        {icon} {title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, textarea, testId, step }) {
  if (textarea) {
    return (
      <label className="block col-span-1 sm:col-span-2">
        <span className="text-xs font-bold text-brand-text2 block mb-1">{label}</span>
        <textarea
          className="w-full border border-brand bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
          rows={2}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          data-testid={testId}
        />
      </label>
    );
  }
  return (
    <label className="block">
      <span className="text-xs font-bold text-brand-text2 block mb-1">{label}</span>
      <input
        className="w-full border border-brand bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
        type={type}
        step={step}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-brand-text2 block mb-1">{label}</span>
      <select
        className="w-full border border-brand bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
        value={value}
        onChange={onChange}
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
